import { useState } from 'react';
import { ButtonGrey, ButtonWhite } from '../../components/shared/Buttons';
import { FormRow } from '../../components/shared/InputRow';
import { Modal, ModalProps } from '../../components/shared/Modal';
import toast from 'react-hot-toast';
import { useApiMutation } from '../../scripts/api/api_v2';
import { File } from '../../components/shared/File';
import { Text } from '../../components/shared/Text';
import { FlexCol } from '../../components/shared/Flex';
import styled from 'styled-components';
import { API_EndpointResult } from '../../scripts/api/api_v2_def';

const StyledFailedImportsContainer = styled(FlexCol)`
	row-gap: 0.5rem;
`;

export function ModalImport({ closeModal }: ModalProps) {
	const [saving, setSaving] = useState<'saving' | 'saved' | 'unsaved'>('unsaved');
	const [formState, setFormState] = useState<string | undefined>(undefined);
	const [fileName, setFileName] = useState<string | undefined>(undefined);
	const [importResponse, setImportResponse] = useState<API_EndpointResult<'tix.import'> | undefined>(undefined);

	const { fetchData: postSaleAPI } = useApiMutation('tix.import');

	const readCSV = (data: FileList | null) => {
		const file = data?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => {
			const textResult = reader.result as string;
			setFormState(textResult);
			console.log(textResult);
			setFileName(file.name);
		};

		reader.readAsText(file);
	};

	const onClickSave = async () => {
		try {
			if (!formState) return;
			setSaving('saving');

			const savedSaleRes = await postSaleAPI({
				body: { importCSV: formState },
			});
			if (!savedSaleRes?.success) {
				toast.error(savedSaleRes?.error ?? 'Failed to import!');
				setSaving('unsaved');
				return;
			}
			setImportResponse(savedSaleRes.data);
			setSaving('saved');
		} catch (err) {
			console.error(err);
			setSaving('unsaved');
		}
	};

	const onClickRefresh = () => {
		window.location.reload();
	};

	const onClickDownloadTemplate = () => {
		const csvContent = `primary,email,date,event,city,region,reference,section,row,seat,note_aisle,note_side,note_rv,note,bank,amount,native_amount,native_currency,payment_date\n,,,,,,,,,,,,,,,,,,`;

		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

		const url = URL.createObjectURL(blob);

		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', 'data.csv');

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	return (
		<Modal title="Import Orders and Sales" closeModal={closeModal}>
			<FormRow>
				<File
					typeText="Import CSV"
					onFileDrop={readCSV}
					fileTypes={['.csv']}
					fileSelected={fileName}
					disabled={saving !== 'unsaved'}
				/>
			</FormRow>
			<FormRow>
				<ButtonGrey onClick={onClickDownloadTemplate}>Download Template</ButtonGrey>
			</FormRow>

			{importResponse && importResponse.unsavedOrders.length > 0 && (
				<FormRow>
					<StyledFailedImportsContainer>
						<Text $color="text_desc_main" $size="1.6rem" $weight="600">
							Failed imports
						</Text>
						{importResponse.unsavedOrders.map((failedOrder, index) => (
							<Text
								$color="text_desc_alt"
								$size="1.3rem"
								$weight="400"
								key={`failed_import_order_${index}`}
							>{`Order ${failedOrder.id} - ${failedOrder.error}`}</Text>
						))}
					</StyledFailedImportsContainer>
				</FormRow>
			)}

			<FormRow>
				<ButtonGrey disabled={saving === 'saving'} onClick={closeModal}>
					Cancel
				</ButtonGrey>
				{saving === 'saved' ? (
					<ButtonWhite onClick={onClickRefresh}>Refresh Page</ButtonWhite>
				) : (
					<ButtonWhite disabled={saving !== 'unsaved'} onClick={onClickSave}>
						Save
					</ButtonWhite>
				)}
			</FormRow>
		</Modal>
	);
}
